import React, { useState, useEffect, useRef } from "react";
import { toast } from 'react-toastify';
import LeftNav from "../common/LeftNav";
import TopNav from "../common/TopNav";
import { useNavigate } from "react-router-dom";
import personalleft from "../../assets/img/personal-left.svg";
import userService from "../../../services/userService";
import "../../assets/styles/HelpSupport.css";
import "../../assets/styles/Policies.css";

const POLICY_TYPES = [
  { label: "Terms & Conditions", value: "policy_terms_and_conditions" },
  { label: "Privacy Policy", value: "policy_privacy_policy" },
  { label: "Cookies Policy", value: "policy_cookies" },
  { label: "Refund Policy", value: "policy_refund_policy" },
];

export default function Policies() {
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState("policy_terms_and_conditions");
  const [content, setContent] = useState("");
  const [allPolicies, setAllPolicies] = useState([]);
  const [currentTermId, setCurrentTermId] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const pendingContent = useRef("");

  // After loading completes, push content into the editor
  useEffect(() => {
    if (!loading && editorRef.current) {
      editorRef.current.innerHTML = pendingContent.current;
    }
  }, [loading]);

  // Load all policy statuses on mount
  useEffect(() => {
    const loadAllStatuses = async () => {
      try {
        const promises = POLICY_TYPES.map((p) => userService.getAdminLegalTerms(p.value));
        const results = await Promise.all(promises);
        const combined = [];
        results.forEach((response) => {
          let data = [];
          if (response?.success && Array.isArray(response?.data)) data = response.data;
          else if (Array.isArray(response?.data?.data)) data = response.data.data;
          combined.push(...data);
        });
        setAllPolicies(combined);
      } catch (e) {
        console.error("Error loading policy statuses:", e);
      }
    };
    loadAllStatuses();
  }, []);

  // Load selected policy content
  useEffect(() => {
    loadPolicy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPolicy]);

  const loadPolicy = async () => {
    try {
      setLoading(true);
      const response = await userService.getAdminLegalTerms(selectedPolicy);

      let termsData = [];
      if (response?.success && Array.isArray(response?.data)) termsData = response.data;
      else if (Array.isArray(response?.data?.data)) termsData = response.data.data;

      setAllPolicies((prev) => {
        const filtered = prev.filter((t) => t.user_type !== selectedPolicy);
        return [...filtered, ...termsData];
      });

      const match = termsData.find((t) => t.user_type === selectedPolicy);
      if (match) {
        setContent(match.content || "");
        setCurrentTermId(match.id);
        pendingContent.current = match.content || "";
      } else {
        setContent("");
        setCurrentTermId(null);
        pendingContent.current = "";
      }
      setHasChanges(false);
    } catch (e) {
      console.error("Error loading policy:", e);
      setContent("");
      setCurrentTermId(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const trimmed = content?.trim();
    if (!trimmed || trimmed === "<br>" || trimmed === "<br/>") {
      toast.warning("Please enter content for the policy.");
      return;
    }

    try {
      setSaving(true);
      const policyLabel = POLICY_TYPES.find((p) => p.value === selectedPolicy)?.label || "Policy";
      const response = await userService.saveLegalTerms({
        title: policyLabel,
        content,
        user_type: selectedPolicy,
      });

      if (response?.success || response?.data?.success) {
        toast.success("Policy saved successfully.");
        setHasChanges(false);
        loadPolicy();
      } else {
        toast.error(response?.message || response?.data?.message || "Failed to save policy.");
      }
    } catch (e) {
      console.error("Error saving policy:", e);
      toast.error("Failed to save policy.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!currentTermId) {
      toast.warning("No saved policy to delete.");
      return;
    }
    try {
      setLoading(true);
      const response = await userService.deleteLegalTerms(currentTermId);
      if (response?.success || response?.data?.success) {
        toast.success("Policy deleted.");
        setContent("");
        setCurrentTermId(null);
        setHasChanges(false);
        if (editorRef.current) editorRef.current.innerHTML = "";
        setAllPolicies((prev) => prev.filter((t) => t.id !== currentTermId));
      } else {
        toast.error("Failed to delete policy.");
      }
    } catch (e) {
      console.error("Error deleting policy:", e);
      alert("Failed to delete policy.");
    } finally {
      setLoading(false);
    }
  };

  const handlePolicyChange = (type) => {
    setSelectedPolicy(type);
    setHasChanges(false);
  };

  const getStatusForType = (type) => allPolicies.some((t) => t.user_type === type);

  const handleImportWord = async (e) => {
    const file = e.target.files?.[0];
    if (!e.target) return;
    e.target.value = '';
    if (!file) return;

    try {
      setImporting(true);
      const response = await userService.convertDocxToHtml(file);
      const html = response?.data?.data?.html || response?.data?.html;

      if (!html || !html.trim()) {
        toast.warning('The document appears to be empty.');
        return;
      }

      if (editorRef.current) {
        editorRef.current.innerHTML = html;
        setContent(html);
        setHasChanges(true);
      }
      toast.success('Document imported with formatting.');
    } catch (error) {
      console.error('Import Word error:', error);
      toast.error('Failed to import Word document. Make sure it is a .docx file.');
    } finally {
      setImporting(false);
    }
  };

  // Rich text toolbar commands
  const execCmd = (command, value = null) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    syncContent();
  };

  const syncContent = () => {
    if (editorRef.current) {
      setContent(editorRef.current.innerHTML);
      setHasChanges(true);
    }
  };

  return (
    <div className="d-flex" style={{ background: "#fafbfc", minHeight: "100vh" }}>
      <LeftNav />
      <div className="main-content flex-grow-1">
        <TopNav />
        <div className="p-4">
          <div className="card p-4 help-card">
            {/* Header */}
            <div className="d-flex align-items-center justify-content-between mb-4">
              <div className="d-flex align-items-center">
                <img
                  src={personalleft}
                  className="me-2"
                  width={28}
                  height={28}
                  alt="back"
                  style={{ cursor: "pointer" }}
                  onClick={() => navigate(-1)}
                />
                <h6 className="mb-0 help-title">Policies</h6>
              </div>
              {currentTermId && (
                <button
                  className="btn btn-sm btn-outline-danger"
                  onClick={handleDelete}
                  disabled={loading}
                  style={{ borderRadius: 8 }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 24 24" style={{ marginRight: 4 }}>
                    <path d="M9 3h6l1 1h4v2H4V4h4l1-1zm-2 5h10l-1 13H8L7 8zm2 2v9h1V10H9zm3 0v9h1V10h-1zm3 0v9h1V10h-1z" />
                  </svg>
                  Delete
                </button>
              )}
            </div>

            {/* Policy Type Tabs */}
            <div className="policies-tab-container mb-3">
              <div className="d-flex flex-wrap gap-2">
                {POLICY_TYPES.map((type) => {
                  const isActive = selectedPolicy === type.value;
                  const hasSaved = getStatusForType(type.value);
                  return (
                    <button
                      key={type.value}
                      onClick={() => handlePolicyChange(type.value)}
                      className={`policies-tab-btn${isActive ? " active" : ""}`}
                    >
                      {type.label}
                      {hasSaved && (
                        <span className={`policies-saved-dot${isActive ? " active" : ""}`} />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Info bar */}
            <div className="policies-info-bar mb-3">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="#6B7280" viewBox="0 0 24 24" style={{ marginRight: 6, flexShrink: 0 }}>
                <path d="M12 2a10 10 0 100 20A10 10 0 0012 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
              </svg>
              <span style={{ fontSize: 12, color: "#6B7280" }}>
                This policy applies to all users (Agent, Agency, Builder)
              </span>
            </div>

            {/* Loading */}
            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <div className="mt-3" style={{ color: '#888' }}>Loading...</div>
              </div>
            ) : (
              <>
                {/* Hidden file input for Word import */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".docx,.doc"
                  style={{ display: 'none' }}
                  onChange={handleImportWord}
                />

                {/* Import from Word button */}
                <div className="policies-import-bar mb-2">
                  <button
                    className="policies-import-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={importing}
                  >
                    {importing ? (
                      <span className="spinner-border spinner-border-sm me-1" role="status" />
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" fill="currentColor" viewBox="0 0 24 24" style={{ marginRight: 5 }}>
                        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM6 20V4h5v7h7v9H6z"/>
                      </svg>
                    )}
                    {importing ? 'Importing...' : 'Import from Word (.docx)'}
                  </button>
                </div>

                {/* Rich Text Toolbar */}
                <div className="policies-toolbar mb-1">
                  <button title="Bold" onClick={() => execCmd("bold")}><b>B</b></button>
                  <button title="Italic" onClick={() => execCmd("italic")}><i>I</i></button>
                  <button title="Underline" onClick={() => execCmd("underline")}><u>U</u></button>
                  <button title="Strike" onClick={() => execCmd("strikeThrough")}><s>S</s></button>
                  <span className="policies-toolbar-divider" />
                  <button title="Heading 1" onClick={() => execCmd("formatBlock", "H1")}>H1</button>
                  <button title="Heading 2" onClick={() => execCmd("formatBlock", "H2")}>H2</button>
                  <button title="Paragraph" onClick={() => execCmd("formatBlock", "P")}>P</button>
                  <span className="policies-toolbar-divider" />
                  <button title="Bullet list" onClick={() => execCmd("insertUnorderedList")}>&#8226; List</button>
                  <button title="Numbered list" onClick={() => execCmd("insertOrderedList")}>1. List</button>
                  <span className="policies-toolbar-divider" />
                  <button title="Align Left" onClick={() => execCmd("justifyLeft")}>&#8676;</button>
                  <button title="Align Center" onClick={() => execCmd("justifyCenter")}>&#8596;</button>
                  <button title="Align Right" onClick={() => execCmd("justifyRight")}>&#8677;</button>
                  <span className="policies-toolbar-divider" />
                  <button title="Undo" onClick={() => execCmd("undo")}>&#8592;</button>
                  <button title="Redo" onClick={() => execCmd("redo")}>&#8594;</button>
                </div>

                {/* Content Editable Editor */}
                <div
                  ref={editorRef}
                  contentEditable
                  suppressContentEditableWarning
                  className="policies-editor"
                  onInput={syncContent}
                  data-placeholder="Enter policy content here..."
                />

                {/* Save Button */}
                <div className="d-flex justify-content-end mt-3">
                  <button
                    className="btn policies-save-btn"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? (
                      <span className="spinner-border spinner-border-sm me-2" role="status" />
                    ) : null}
                    {currentTermId ? "Update" : "Save"} Policy
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
